import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NumberUtils, USER_INPUT_DEBOUNCE_TIME } from '@modeso/aumico-lib-core-fe';
import { IFreeTable, IFreeTableEntry } from '@modeso/types__aumico-lib-annualreport-be';
import { ViewMode } from '@modeso/types__aumico-lib-core-be';
import { IFreeTableOptions } from '@modeso/types__aumico-lib-notes-be';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal';
import { cloneDeep } from 'lodash-es';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { IAnnualReportDefaultSettings } from '../../interfaces/IAnnualReportDefaultSettings.interface';
import { NotesPageBase } from '../NotesPageBase';
import { IFreeTableDelegate } from './IFreeTableDelegate.interface';

const debug = Debug('aumico:aumico-lib-notes-fe:FreeTableWithAmountsComponent');

@Component({
    selector: 'aumico-notes-free-table-with-amounts2',
    templateUrl: 'freeTableWithAmounts2.component.html',
    styleUrls: ['freeTableWithAmounts2.component.scss'],
})
export class FreeTableWithAmountsComponent2 extends NotesPageBase implements OnInit, OnChanges {
    @Input() componentIndex: number;

    @Input() public set delegate(delegate: IFreeTableDelegate) {
        if (delegate) {
            this.DELEGATE = delegate;
        }
    }
    DELEGATE?: IFreeTableDelegate;

    @Input() editable = true;

    // --- Required Inputs ---
    FreeTableData: IFreeTable = {
        currentDate: '2021-12-31T22:00:00.000Z',
        previousDate: '2020-12-31T22:00:00.000Z',
        currency: 'CHF',
        options: { totalRow: true, title: {} },
        rows: [],
        totalRow: { text: 'Total', currentValue: 0, previousValue: 0 },
    };
    @Input() set freeTableData(value: IFreeTable) {
        if (value != null) {
            this.FreeTableData = cloneDeep<IFreeTable>(value);
            this.totalRowTextCell = this.FreeTableData.totalRow ? this.FreeTableData.totalRow.text : 'Total';
        }
    }

    @Input() set annualReportDefaultSettings(value: IAnnualReportDefaultSettings) {
        this.FreeTableData = {
            ...this.FreeTableData,
            currentDate: value.currentBalanceDate,
            previousDate: value.previousBalanceDate,
            currency: value.currency,
        };
        if (this.form) {
            this.form.get('headers')?.get('currentValue').setValue(value.currentBalanceDate, { emitEvent: false });
            this.form.get('headers')?.get('previousValue').setValue(value.previousBalanceDate, { emitEvent: false });
        }

    }

    ComponentOptions: IFreeTableOptions;
    @Input() set componentOptions(value: IFreeTableOptions) {
        if (value != null) {
            this.ComponentOptions = value;
            this.displayTotalRow = this.ComponentOptions.totalRow;
        } else {
            this.ComponentOptions = this.FreeTableData.options;
            this.displayTotalRow = this.ComponentOptions.totalRow;
        }
    }

    @Input() public language: string = 'de-ch';
    @Input() viewMode: ViewMode = ViewMode.EDIT;
    @Input() highlightTodos = false;
    @Input() tableNumber: string | undefined;
    @Input() isReferenced = false; // Passed to ham-referencing-input-component
    @Input() isNumbered = false; // Passed to ham-referencing-input-component
    // Add footnote preservation mechanism similar to movement schedule component
    private currentFootnoteValue: string = '';

    @Input() override hasFootnotes = false; // Input to enable footnote functionality

    // Track hasFootnotes changes to preserve footnote values
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['hasFootnotes']) {
            const currentValue = changes['hasFootnotes'].currentValue;
            const previousValue = changes['hasFootnotes'].previousValue;

            // If footnotes are being disabled, preserve the current footnote value
            if (!currentValue && previousValue && this.form?.get('footnote')) {
                this.currentFootnoteValue = this.form.get('footnote')?.value || '';
            }

            // If footnotes are being enabled after being disabled, we need to reinitialize the form
            if (currentValue && !previousValue && this.form) {
                // Use preserved footnote value, then saved footnote, then fallback to empty
                const footnoteValue =
                    this.currentFootnoteValue || this.savedFootnote || this.FreeTableData.footnotes?.[0] || '';
                this.form.addControl('footnote', new FormControl(footnoteValue));
            }
        }

        if (changes['language'] && changes['language'].currentValue != null && this.form != null) {
            // Update header title when language changes
            this.form
                .get('headers')
                .get('text')
                ?.setValue(
                    this.ComponentOptions && this.ComponentOptions.title
                        ? this.ComponentOptions.title[this.language]
                        : '',
                );
        }
    }
    // --- End Required Inputs ---

    private _savedFootnote: string | undefined;
    @Input() set savedFootnote(value: string | undefined) {
        this._savedFootnote = value;
        if (value !== undefined) {
            this.currentFootnoteValue = value || '';
        }
        if (this.form && this.form.get('footnote')) {
            this.form.get('footnote')?.setValue(this.currentFootnoteValue, { emitEvent: false });
        }
    }
    get savedFootnote(): string | undefined {
        return this._savedFootnote;
    }

    // A default label for the total row (editable)
    totalRowTextCell: string = 'Total';

    // Flag that determines if the total row is displayed
    displayTotalRow: boolean = true;

    // Delegate data updated as the form changes
    dataToDelegate: IFreeTable;

    // Reactive form: headers, FormArray for rows, and optionally a total row group.
    form: FormGroup;

    constructor(private fb: FormBuilder) {
        super();
    }

    public toggleTotalRow() {
        this.displayTotalRow = !this.displayTotalRow;
    }

    /**
     * Handle footnote changes
     */
    private handleFootnoteChanges(footnoteValue: string) {
        // Always update the preserved footnote value
        this.currentFootnoteValue = footnoteValue || '';
    }

    ngOnInit(): void {
        debug('ngOnInit');
        this.initForm();
        this.subscribeFormChanges();

        // Calculate totals immediately after form initialization
        this.calculateAndSetTotals();
    }

    /**
     * Build the reactive form with static keys.
     * - headers: "text", "currentValue", "previousValue"
     * - dataSource: a FormArray of data rows
     * - totalRow: computed totals for "currentValue" and "previousValue" and an editable label in "text"
     */
    initForm() {
        const headersGroup = this.fb.group({
            text: new FormControl(
                this.ComponentOptions && this.ComponentOptions.title && this.ComponentOptions.title[this.language],
            ),
            currentValue: new FormControl(this.FreeTableData.currentDate),
            previousValue: new FormControl(this.FreeTableData.previousDate),
        });

        if (!this.FreeTableData.rows || this.FreeTableData.rows.length === 0) {
            this.FreeTableData.rows = [this.createEmptyRow()];
        }
        const dataSourceArray = this.fb.array(this.FreeTableData.rows.map((row) => this.createRowGroup(row)));

        const group: any = {
            headers: headersGroup,
            dataSource: dataSourceArray,
        };

        group.totalRow = this.createTotalRowGroup();

        // Add footnote control if footnotes are enabled
        if (this.hasFootnotes) {
            // Use preserved footnote value, then saved footnote, then fallback to empty
            const footnoteValue =
                this.currentFootnoteValue || this.savedFootnote || this.FreeTableData.footnotes?.[0] || '';
            group.footnote = new FormControl(footnoteValue);
        }

        this.form = this.fb.group(group);
    }

    /**
     * Creates an empty data row.
     */
    createEmptyRow(): any {
        return { text: '', currentValue: '', previousValue: '' };
    }

    /**
     * Creates a FormGroup for a single data row.
     */
    createRowGroup(rowData: any): FormGroup {
        return this.fb.group({
            text: new FormControl(rowData.text),
            currentValue: new FormControl(rowData.currentValue),
            previousValue: new FormControl(rowData.previousValue),
        });
    }

    /**
     * Creates the total row group.
     * The "text" field is editable.
     */
    createTotalRowGroup(): FormGroup {
        return this.fb.group({
            text: new FormControl(this.totalRowTextCell, Validators.required),
            currentValue: new FormControl({ value: 0, disabled: true }, Validators.required),
            previousValue: new FormControl({ value: 0, disabled: true }, Validators.required),
        });
    }

    /**
     * Recalculate totals for the currentValue and previousValue columns
     * and update the totalRow group accordingly.
     */
    calculateAndSetTotals() {
        const formValue = this.form.getRawValue();
        const dataRows: IFreeTableEntry[] = formValue.dataSource;
        if (formValue.totalRow) {
            // Always calculate totals regardless of displayTotalRow flag.
            const entriesCurrent = dataRows.map((row) => +row.currentValue || 0);
            const entriesPrevious = dataRows.map((row) => +row.previousValue || 0);
            const sumCurrent = NumberUtils.sumIgnoringInfiniteNumbers(entriesCurrent, 0);
            const sumPrevious = NumberUtils.sumIgnoringInfiniteNumbers(entriesPrevious, 0);
            // Setting the totals without emitting an event avoids creating loops.
            this.form.get('totalRow').get('currentValue').setValue(sumCurrent, { emitEvent: false });
            this.form.get('totalRow').get('previousValue').setValue(sumPrevious, { emitEvent: false });
        }
    }

    /**
     * Subscribe to form changes: recalculate totals and update delegate data.
     */
    subscribeFormChanges() {
        this.form.valueChanges
            .pipe(
                debounceTime(USER_INPUT_DEBOUNCE_TIME),
                distinctUntilChanged((prev, curr) => fastDeepEqual(prev, curr)),
            )
            .subscribe(() => {
                // Update totals on every change
                this.calculateAndSetTotals();

                const formValue = this.form.getRawValue();
                const dataRows: IFreeTableEntry[] = formValue.dataSource;
                const totalRow: IFreeTableEntry = formValue.totalRow;

                // Handle footnote changes regardless of hasFootnotes state
                // This ensures we always track the current footnote value
                if (formValue.footnote !== undefined) {
                    this.handleFootnoteChanges(formValue.footnote);
                }

                this.onFormChange({
                    formValue: dataRows,
                    totalRowValue: totalRow,
                    headers: formValue.headers,
                    footnote: formValue.footnote,
                });
            });
    }

    /**
     * Called on every form change to update delegate data.
     */
    private onFormChange(form: any, footnote?: string): void {
        const headerText = form.headers.text;
        this.totalRowTextCell = form.totalRowValue ? form.totalRowValue.text : 'Total';

        this.dataToDelegate = {
            currency: this.FreeTableData.currency,
            currentDate: this.FreeTableData.currentDate,
            previousDate: this.FreeTableData.previousDate,
            options: {
                ...this.ComponentOptions,
                title: { [this.language]: headerText },
                totalRow: this.displayTotalRow,
            },
            rows: form.formValue,
            totalRow: form.totalRowValue,
            addNumbering: this.addNumbering,
            referenceFlis: this.referenceFlis,
        };

        // Handle footnotes separately to ensure proper saving
        if (this.hasFootnotes) {
            // When footnotes are enabled, use the current form value (form.footnote should always exist)
            const currentFootnote = form.footnote;
            if (currentFootnote && currentFootnote.trim() !== '') {
                this.dataToDelegate.footnotes = [currentFootnote];
            } else {
                this.dataToDelegate.footnotes = [];
            }
        } else {
            // When footnotes are disabled, preserve the existing footnotes using currentFootnoteValue
            // This ensures that footnote data is not lost when the toggle is off
            if (this.currentFootnoteValue && this.currentFootnoteValue.trim() !== '') {
                this.dataToDelegate.footnotes = [this.currentFootnoteValue];
            } else {
                // Fallback to existing footnotes data if no preserved value
                this.dataToDelegate.footnotes = this.FreeTableData.footnotes || [];
            }
        }

        debug('freeTableData', this.dataToDelegate);
        if (this.DELEGATE) {
            this.DELEGATE.freeTableComponentData(this.dataToDelegate, this.componentIndex);
        }
    }

    /**
     * Called when the "Add New Item" button is clicked.
     */
    public addItem() {
        const emptyRow = this.createEmptyRow();
        this.FreeTableData.rows.push(emptyRow);
        (this.form.get('dataSource') as FormArray).push(this.createRowGroup(emptyRow));
    }

    /**
     * Removes a data row at the given index.
     */
    removeRow(index: number) {
        this.FreeTableData.rows.splice(index, 1);
        (this.form.get('dataSource') as FormArray).removeAt(index);
    }

    /**
     * Handler for title changes from ham-referencing-input-component.
     */
    onTitleChanged(newTitle: string): void {
        this.form.get('headers.text')?.setValue(newTitle);
        this.onFormChange({
            formValue: this.form.get('dataSource')?.value,
            totalRowValue: this.form.get('totalRow')?.value,
            headers: this.form.get('headers')?.value,
            footnote: this.hasFootnotes ? this.form.get('footnote')?.value : undefined,
        });
    }
}
